import { createContext, useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment/moment';
import {
  getAllReferralCodesFromCookie,
  updateCookieReferralCodes,
} from '@/helpers/referral';
import { checkReferralCode } from '@/store/services/referralService';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

const ReferralContext = createContext({
  codes: [],
  currentRefCode: null,
});

const CODE_EXPIRE_DAYS = 30;

export const ReferralProvider = ({ children }) => {
  const location = useLocation();
  const [codes, setCodes] = useState(getAllReferralCodesFromCookie() || []);
  const checkedRefs = useRef([]);
  const { ref } = qs.parse(location.search.replace('?', ''));

  const removeFromCodes = code =>
    setCodes(prev => prev.filter(c => c.id !== code));

  useEffect(() => {
    ref && saveReferralCode(ref);
  }, [ref]);

  const saveReferralCode = async code => {
    const res = await checkReferralCode(code);
    if (res.success) {
      checkedRefs.current.push(code);
      const existingCode = codes.find(c => c.id === code);

      if (!res.data.is_valid) {
        existingCode && removeFromCodes(code);
        console.error(`Referral code ${code} is invalid`);
        return;
      }

      const codeData = {
        ...res.data,
        id: code,
        expire_date:
          existingCode?.expire_date ||
          moment().add(CODE_EXPIRE_DAYS, 'days').toISOString(),
        created_at: existingCode?.created_at || moment().toISOString(),
      };
      setCodes(prev =>
        existingCode
          ? prev.map(item => {
              if (item.id === existingCode.id) return codeData;
              return item;
            })
          : [...prev, codeData],
      );
    }
  };

  useEffect(() => {
    updateCookieReferralCodes(codes);
  }, [codes]);

  const lastCodeData = codes[codes.length - 1];

  useEffect(() => {
    lastCodeData &&
      !checkedRefs.current.includes(lastCodeData.id) &&
      validateCode(lastCodeData);
  }, [lastCodeData]);

  const validateCode = async codeData => {
    const res = await checkReferralCode(codeData.id);
    if (res.success) {
      checkedRefs.current.push(codeData.id);
      if (!res.data.is_valid || moment().isAfter(codeData.expire_date)) {
        // Remove expired or not-valid code from state
        return removeFromCodes(codeData.id);
      }

      const updatedCode = {
        ...res.data,
        id: codeData.id,
        expire_date:
          codeData?.expire_date ||
          moment().add(CODE_EXPIRE_DAYS, 'days').toISOString(),
        created_at: codeData?.created_at || moment().toISOString(),
      };

      setCodes(prev =>
        prev.map(item => {
          if (item.id === codeData.id) return updatedCode;
          return item;
        }),
      );
    }
  };

  const value = {
    refCodes: codes,
    currentRefCode: lastCodeData,
  };

  return (
    <ReferralContext.Provider value={value}>
      {children}
    </ReferralContext.Provider>
  );
};

export const useReferrals = () => {
  const referrals = useContext(ReferralContext);
  if (!referrals) {
    throw new Error('useReferrals must be used within a ReferralProvider');
  }
  return referrals;
};
